import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../lib/adminApi';
import { toast } from 'sonner';
import { GestorePageHeader } from '../../components/gestore/GestorePageHeader';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Save, KeySquare, Map, Mail, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const SourceBadge = ({ info }) => {
  const { t } = useTranslation();
  if (!info?.has) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground" data-testid="key-status-none">
        <AlertCircle className="h-3.5 w-3.5" /> {t('gestore.intNotSet', { defaultValue: 'Non configurata' })}
      </span>
    );
  }
  const isEnv = info.source === 'env';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${isEnv ? 'bg-amber-500/15 text-amber-600' : 'bg-emerald-500/15 text-emerald-600'}`} data-testid="key-status-set">
      <CheckCircle2 className="h-3.5 w-3.5" />
      {isEnv ? t('gestore.intFromEnv', { defaultValue: 'Da ambiente (non persistente)' }) : t('gestore.intFromDb', { defaultValue: 'Salvata (persistente)' })}
    </span>
  );
};

const SecretField = ({ id, label, placeholder, value, onChange, currentMasked, hint }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label htmlFor={id} className="mb-1.5 block">{label}</Label>
      <div className="relative">
        <Input
          id={id}
          data-testid={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={currentMasked ? currentMasked : placeholder}
          className="pr-10"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          tabIndex={-1}
          aria-label={show ? 'hide' : 'show'}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
};

export default function GestoreIntegrations() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  // Campi editabili (vuoti = non modificare le chiavi segrete esistenti)
  const [llm, setLlm] = useState('');
  const [geo, setGeo] = useState('');
  const [smtp, setSmtp] = useState({ smtp_host: '', smtp_port: 587, smtp_security: 'starttls', smtp_username: '', smtp_password: '', mail_from: '', mail_from_name: '', leads_notify_email: '' });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/admin/integrations');
      setInfo(data);
      setSmtp({
        smtp_host: data.smtp?.smtp_host || '',
        smtp_port: data.smtp?.smtp_port || 587,
        smtp_security: data.smtp?.smtp_security || 'starttls',
        smtp_username: data.smtp?.smtp_username || '',
        smtp_password: '',
        mail_from: data.smtp?.mail_from || '',
        mail_from_name: data.smtp?.mail_from_name || '',
        leads_notify_email: data.smtp?.leads_notify_email || '',
      });
      setLlm(''); setGeo('');
    } catch (e) {
      toast.error(t('gestore.intLoadError', { defaultValue: 'Errore nel caricamento' }));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...smtp };
      // Invia le chiavi segrete solo se l'utente ha inserito un nuovo valore.
      if (llm.trim()) payload.emergent_llm_key = llm.trim();
      if (geo.trim()) payload.geoapify_key = geo.trim();
      if (!smtp.smtp_password) delete payload.smtp_password;
      const { data } = await adminApi.put('/admin/integrations', payload);
      setInfo(data);
      setSmtp((s) => ({ ...s, smtp_password: '' }));
      setLlm(''); setGeo('');
      toast.success(t('gestore.intSaved', { defaultValue: 'Chiavi salvate' }));
    } catch (e) {
      toast.error(t('gestore.intSaveError', { defaultValue: 'Errore nel salvataggio' }));
    } finally { setSaving(false); }
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6 max-w-3xl" data-testid="gestore-integrations-page">
      <GestorePageHeader
        title={t('gestore.intTitle', { defaultValue: 'Chiavi API di sistema' })}
        subtitle={t('gestore.intSubtitle', { defaultValue: 'Chiavi globali salvate nel database, valide per tutta la piattaforma e persistenti ai deploy.' })}
      >
        <Button onClick={save} disabled={saving || loading} className="rounded-xl" data-testid="integrations-save-button">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          {saving ? t('gestore.sSaving', { defaultValue: 'Salvataggio...' }) : t('gestore.sSave', { defaultValue: 'Salva' })}
        </Button>
      </GestorePageHeader>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="space-y-5">
          {/* EMERGENT LLM KEY */}
          <Card className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><KeySquare className="h-5 w-5" /></div>
                <div>
                  <h3 className="font-semibold leading-tight">{t('gestore.intLlmTitle', { defaultValue: 'Chiave AI (Emergent LLM)' })}</h3>
                  <p className="text-sm text-muted-foreground">{t('gestore.intLlmDesc', { defaultValue: 'Usata per traduzioni menu, import menu AI e grafica AI della carta.' })}</p>
                </div>
              </div>
              <SourceBadge info={info?.emergent_llm_key} />
            </div>
            <div className="mt-4">
              <SecretField
                id="integration-llm-key"
                label={t('gestore.intLlmLabel', { defaultValue: 'EMERGENT_LLM_KEY' })}
                placeholder="sk-emergent-..."
                value={llm}
                onChange={setLlm}
                currentMasked={info?.emergent_llm_key?.masked}
                hint={t('gestore.intSecretHint', { defaultValue: 'Lascia vuoto per non modificare il valore attuale.' })}
              />
            </div>
          </Card>

          {/* GEOAPIFY */}
          <Card className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Map className="h-5 w-5" /></div>
                <div>
                  <h3 className="font-semibold leading-tight">{t('gestore.intGeoTitle', { defaultValue: 'Chiave Mappe (Geoapify)' })}</h3>
                  <p className="text-sm text-muted-foreground">{t('gestore.intGeoDesc', { defaultValue: 'Stile delle mappe (tavoli/prenotazioni). Se assente si usa OpenStreetMap.' })}</p>
                </div>
              </div>
              <SourceBadge info={info?.geoapify_key} />
            </div>
            <div className="mt-4">
              <SecretField
                id="integration-geoapify-key"
                label={t('gestore.intGeoLabel', { defaultValue: 'Geoapify API Key' })}
                placeholder="xxxxxxxxxxxxxxxx"
                value={geo}
                onChange={setGeo}
                currentMasked={info?.geoapify_key?.masked}
                hint={t('gestore.intSecretHint', { defaultValue: 'Lascia vuoto per non modificare il valore attuale.' })}
              />
            </div>
          </Card>

          {/* SMTP */}
          <Card className="p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Mail className="h-5 w-5" /></div>
              <div>
                <h3 className="font-semibold leading-tight">{t('gestore.intSmtpTitle', { defaultValue: 'Email di sistema (SMTP)' })}</h3>
                <p className="text-sm text-muted-foreground">{t('gestore.intSmtpDesc', { defaultValue: 'Server email usato per inviare le notifiche (es. conferme prenotazioni) a tutti i ristoranti.' })}</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label htmlFor="smtp-host" className="mb-1.5 block">{t('gestore.intSmtpHost', { defaultValue: 'Host SMTP' })}</Label>
                <Input id="smtp-host" data-testid="smtp-host" value={smtp.smtp_host} onChange={(e) => setSmtp({ ...smtp, smtp_host: e.target.value })} placeholder="smtp.gmail.com" autoComplete="off" />
              </div>
              <div>
                <Label htmlFor="smtp-port" className="mb-1.5 block">{t('gestore.intSmtpPort', { defaultValue: 'Porta' })}</Label>
                <Input id="smtp-port" data-testid="smtp-port" type="number" value={smtp.smtp_port} onChange={(e) => setSmtp({ ...smtp, smtp_port: Number(e.target.value) })} placeholder="587" />
              </div>
              <div>
                <Label className="mb-1.5 block">{t('gestore.intSmtpSecurity', { defaultValue: 'Sicurezza' })}</Label>
                <Select value={smtp.smtp_security} onValueChange={(v) => setSmtp({ ...smtp, smtp_security: v })}>
                  <SelectTrigger data-testid="smtp-security"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starttls">STARTTLS</SelectItem>
                    <SelectItem value="ssl">SSL/TLS</SelectItem>
                    <SelectItem value="none">{t('gestore.intSmtpNone', { defaultValue: 'Nessuna' })}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="smtp-username" className="mb-1.5 block">{t('gestore.intSmtpUser', { defaultValue: 'Utente' })}</Label>
                <Input id="smtp-username" data-testid="smtp-username" value={smtp.smtp_username} onChange={(e) => setSmtp({ ...smtp, smtp_username: e.target.value })} placeholder="user@dominio.com" autoComplete="off" />
              </div>
              <div>
                <SecretField
                  id="smtp-password"
                  label={t('gestore.intSmtpPass', { defaultValue: 'Password' })}
                  placeholder="********"
                  value={smtp.smtp_password}
                  onChange={(v) => setSmtp({ ...smtp, smtp_password: v })}
                  currentMasked={info?.smtp?.has_password ? '••••••••' : ''}
                />
              </div>
              <div>
                <Label htmlFor="mail-from" className="mb-1.5 block">{t('gestore.intMailFrom', { defaultValue: 'Email mittente' })}</Label>
                <Input id="mail-from" data-testid="mail-from" value={smtp.mail_from} onChange={(e) => setSmtp({ ...smtp, mail_from: e.target.value })} placeholder="noreply@dominio.com" autoComplete="off" />
              </div>
              <div>
                <Label htmlFor="mail-from-name" className="mb-1.5 block">{t('gestore.intMailFromName', { defaultValue: 'Nome mittente' })}</Label>
                <Input id="mail-from-name" data-testid="mail-from-name" value={smtp.mail_from_name} onChange={(e) => setSmtp({ ...smtp, mail_from_name: e.target.value })} placeholder="Vexel POS" autoComplete="off" />
              </div>
              <div className="sm:col-span-2 border-t border-border pt-4 mt-1">
                <Label htmlFor="leads-notify-email" className="mb-1.5 block">{t('gestore.intLeadsNotify', { defaultValue: 'Email notifiche richieste (landing)' })}</Label>
                <Input id="leads-notify-email" data-testid="leads-notify-email" value={smtp.leads_notify_email} onChange={(e) => setSmtp({ ...smtp, leads_notify_email: e.target.value })} placeholder="richieste@dominio.com" autoComplete="off" />
                <p className="mt-1 text-xs text-muted-foreground">{t('gestore.intLeadsNotifyHint', { defaultValue: 'Riceverai una email a questo indirizzo per ogni nuova richiesta “Richiedi ora” dalla landing. Se vuoto, usa l\u2019email mittente.' })}</p>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
